import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Mail, 
  FolderOpen, 
  HelpCircle, 
  Award,
  Image,
  Phone,
  LogOut,
  Home,
  Settings,
  Menu,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchStats(token);
  }, [navigate]);

  const fetchStats = async (token) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Déconnexion réussie');
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Home, label: 'Contenu Homepage', path: '/admin/cms/homepage' },
    { icon: Settings, label: 'Pages Solutions', path: '/admin/cms/solutions' },
    { icon: FolderOpen, label: 'Galerie Projets', path: '/admin/cms/projects', badge: stats.projects },
    { icon: HelpCircle, label: 'FAQ', path: '/admin/cms/faq', badge: stats.faq },
    { icon: Award, label: 'Certifications', path: '/admin/cms/certifications' },
    { divider: true },
    { icon: FileText, label: 'Demandes Devis', path: '/admin/quotes', badge: stats.quotes },
    { icon: Mail, label: 'Messages Contact', path: '/admin/contacts', badge: stats.contacts },
    { icon: Phone, label: 'Demandes Rappel', path: '/admin/callbacks', badge: stats.callbacks },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Sidebar */}
      <div style={{
        width: sidebarOpen ? '280px' : '0',
        minWidth: sidebarOpen ? '280px' : '0',
        background: 'var(--brand-dark)',
        color: 'white',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>Nouveau Départ</h2>
          <p style={{ fontSize: '12px', opacity: 0.7 }}>Administration CMS</p>
        </div>

        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {menuItems.map((item, index) => {
            if (item.divider) {
              return <div key={index} style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '16px 0' }} />;
            }
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <Link
                key={index}
                to={item.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  marginBottom: '4px',
                  borderRadius: '8px',
                  background: active ? 'rgba(211, 255, 98, 0.2)' : 'transparent',
                  color: active ? '#D3FF62' : 'white',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'all 0.2s'
                }}
              >
                <Icon size={18} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.badge > 0 && (
                  <span style={{
                    background: '#D3FF62',
                    color: '#004534',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '2px 8px',
                    borderRadius: '10px'
                  }}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div style={{ padding: '16px 12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Link
            to="/"
            target="_blank"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              marginBottom: '8px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              color: 'white',
              textDecoration: 'none',
              fontSize: '14px'
            }}
          >
            <Image size={18} />
            <span>Voir le site</span>
          </Link>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              width: '100%'
            }}
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top Bar */}
        <div style={{
          background: 'white',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px'
            }}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <h1 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Back-Office CMS
          </h1>
        </div>

        {/* Content Area */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
